import type { CoreError } from '@ingenico/api-sdk-core/common';
import {
  ALL_LNGS_NAMESPACES,
  LNGS_NAMESPACE_CALL_LOGS,
  LNGS_NAMESPACE_LOGICAL_DEVICE,
} from '@ingenico/util-i18n-config';
import { BasicDialogModal } from '@ingenico/react-ui/basic-dialog-modal';
import { CoreAlert } from '@ingenico/react-ui/core-alert';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { isEmpty, toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SoftwareType } from '../../domain/models/selectItems.model';
import type { ModuleItemField } from '../../domain/models/snapshot.model';
import { ModuleType } from '../../domain/models/snapshot.model';
import { useDeviceManagementError } from '../../hooks';
import { ParameterOutputFileModal } from '../parameter-output-file-modal/ParameterOutputFileModal.ctrl';
import { ModuleField } from './components/ModuleField/ModuleField.ctrl';
import { SoftwareNameField } from './components/SoftwareNameField/SoftwareNameField.ctrl';
import { SoftwareTypeField } from './components/SoftwareTypeField/SoftwareTypeField.ctrl';
import { VersionField } from './components/VersionField/VersionField.ctrl';
import styles from './FileOutputFormularyModal.module.scss';
import { useFileOutputFormularyData } from './useFileOutputFormularyData.hook';
import { FileOutputFormularyModalSchemaKeys, useFileOutputFormularyModal } from './useFileOutputFormularyModal.hook';

interface FileOutputFormularyModalProps {
  readonly isOpen: boolean;
  readonly logicalDeviceId: string;
  readonly onClose: () => void;
}

export function FileOutputFormularyModal({
  isOpen,
  logicalDeviceId,
  onClose,
}: FileOutputFormularyModalProps): ReactNode {
  const { t } = useTranslation([
    ...Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE),
    ...Object.values(LNGS_NAMESPACE_CALL_LOGS),
  ]);
  const { getErrorMessage } = useDeviceManagementError();
  const form = useFileOutputFormularyModal({ isOpen });

  const [error, setError] = useState<CoreError | null>(null);

  const onError = useCallback((err: CoreError | null) => {
    setError(err);
  }, []);

  const [softwareType, softwareNameId, versionId] = form.watch([
    FileOutputFormularyModalSchemaKeys.SoftwareTypeField,
    FileOutputFormularyModalSchemaKeys.SoftwareNameIdField,
    FileOutputFormularyModalSchemaKeys.VersionIdField,
  ]);

  const { softwareNames, versions, modules } = useFileOutputFormularyData({
    softwareType,
    softwareNameId,
    versionId,
    onError,
  });

  const handleSoftwareNameFilterChange = useCallback(
    (value: string) => softwareNames.setFilter(value),
    [softwareNames],
  );
  const handleVersionFilterChange = useCallback((value: string) => versions.setFilter(value), [versions]);
  const handleModuleFilterChange = useCallback((value: string) => modules.setFilter(value), [modules]);

  const [configurationModuleIdToTriggerRequest, setConfigurationModuleIdToTriggerRequest] = useState('');
  const allFormFields = form.getValues();

  const errorMessage = useMemo(() => {
    if (error !== null) {
      return getErrorMessage(error, t('sections.parameterOutput.error.default', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS }));
    }
    return '';
  }, [error, getErrorMessage, t]);

  const onCleanConfigurationModuleId = useCallback(() => {
    setConfigurationModuleIdToTriggerRequest('');
  }, []);

  const onCloseCb = useCallback(() => {
    setConfigurationModuleIdToTriggerRequest('');
    setError(null);
    onClose();
  }, [onClose]);

  const onSubmission = useCallback(async () => {
    await form.handleSubmit((formulary) => {
      setConfigurationModuleIdToTriggerRequest(formulary.moduleId);
    })();
  }, [form]);

  const moduleItem = useMemo<ModuleItemField | undefined>(() => {
    if (isEmpty(configurationModuleIdToTriggerRequest)) {
      return undefined;
    }

    const versionData = versions.options.find(({ id }) => allFormFields.versionId === id);
    const moduleData = modules.options.find(({ id }) => allFormFields.moduleId === id);
    return {
      name: toString(moduleData?.label),
      configurationModuleId: toString(allFormFields.moduleId),
      templateRequestId: toString(allFormFields?.versionId),
      version: toString(versionData?.label),
      sourceType:
        allFormFields.softwareType === SoftwareType.Application ? ModuleType.APPLICATION : ModuleType.SYSTEM_PACKAGE,
    };
  }, [
    allFormFields.moduleId,
    allFormFields.softwareType,
    allFormFields.versionId,
    modules.options,
    versions.options,
    configurationModuleIdToTriggerRequest,
  ]);

  return (
    <>
      <BasicDialogModal
        isOpen={isOpen && moduleItem === undefined}
        onIconClick={onCloseCb}
        actions={
          <div className={styles.modalActions}>
            <CoreButton variant="text" onClick={onCloseCb}>
              {t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
            </CoreButton>
            <CoreButton
              disabled={!form?.formState?.isValid}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={onSubmission}
              variant="contained"
            >
              {t('fileOutputModal.button.submission', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
            </CoreButton>
          </div>
        }
        title={t('fileOutputModal.title')}
        hasCloseIcon
        hasDivider
      >
        <div className={styles.modalContent}>
          {errorMessage ? (
            <CoreAlert
              severity={'Error'}
              title={t('sections.parameterOutput.error.title', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
              message={errorMessage}
            />
          ) : (
            <>
              <CoreAlert
                severity={'Info'}
                title={t('fileOutputModal.banner.title')}
                message={t('fileOutputModal.banner.subtitle')}
              />
              <SoftwareTypeField
                form={form}
                softwareNameFilterChange={handleSoftwareNameFilterChange}
                versionFilterChange={handleVersionFilterChange}
                moduleFilterChange={handleModuleFilterChange}
              />
              <SoftwareNameField
                form={form}
                options={softwareNames.options}
                isLoading={softwareNames.isLoading}
                onFilterChange={handleSoftwareNameFilterChange}
                versionFilterChange={handleVersionFilterChange}
                moduleFilterChange={handleModuleFilterChange}
              />
              <VersionField
                form={form}
                options={versions.options}
                isLoading={versions.isLoading}
                onFilterChange={handleVersionFilterChange}
                moduleFilterChange={handleModuleFilterChange}
              />
              <ModuleField
                form={form}
                options={modules.options}
                isLoading={modules.isLoading}
                onFilterChange={handleModuleFilterChange}
              />
            </>
          )}
        </div>
      </BasicDialogModal>
      <ParameterOutputFileModal
        isOpen={!isEmpty(configurationModuleIdToTriggerRequest)}
        deploymentUnitId=""
        moduleItemField={moduleItem}
        onCancel={onCloseCb}
        onBack={onCleanConfigurationModuleId}
        deviceId={logicalDeviceId}
        configurationId={configurationModuleIdToTriggerRequest}
      />
    </>
  );
}
